import { ToastContext } from '@/contexts/ToastContext'
import { ButtonLabel, HeadingLabel, ParagraphLabel, PrimaryLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { ActiveSubscriptionsResponse } from '@grandstand/presentation-models'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useContext, useState } from 'react'
import styled from 'styled-components'
import { GrandstandButton } from '../Buttons'
import PopupModal, { PopupModalProps } from '../modals/PopupModal'

type DeleteAccountProps = {
  returnBack: () => void
  subscriptions: ActiveSubscriptionsResponse
  setFieldToShow: Dispatch<SetStateAction<string>>
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
`

const DeleteAccountPartWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 24px;
`

const BackIcon = styled.div`
  cursor: pointer;
`

const DeleteAccountHeading = styled(HeadingLabel)`
  color: var(--on-background);
  flex-grow: 1;
  text-align: center;
`

const DeleteAccountDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--divider-canvas);
`

const DeleteAccountContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 32px;
  background: var(--surface);
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
`

const DeleteAccountInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  @media (min-width: 768px) {
    width: 480px;
  }
  flex: none;
  order: 0;
  flex-grow: 0;
`

const DeleteAccountInfoHeading = styled(PrimaryLabel)`
  text-transform: uppercase;
  color: var(--on-background);
`

const DeleteAccountInfoContent = styled(ParagraphLabel)`
  flex: none;
  order: 2;
  flex-grow: 0;
`

const DeleteButton = styled(GrandstandButton)`
  width: 100%;
  border-radius: 4px;
  flex: none;
  order: 1;
  flex-grow: 0;
`

const ButtonText = styled(ButtonLabel)`
  text-transform: uppercase;
`

type DeleteConfirmModalProps = PopupModalProps & {
  onConfirm: () => void
  onClose: () => void
}

const DeleteConfirmModal = ({ onConfirm, ...props }: DeleteConfirmModalProps) => {
  return (
    <PopupModal {...props} showCloseButton={true}>
      <PopupModal.Title fontSize="3" mobileFontSize="2">
        Are you sure you want to delete your account?
      </PopupModal.Title>
      <PopupModal.Subtitle fontSize="1">
        This account will no longer be available, all your data in the account will be deleted permanently.
      </PopupModal.Subtitle>
      <PopupModal.Button buttonStyle="primary" buttonSize="sm" onClick={onConfirm}>
        Yes, delete my account
      </PopupModal.Button>
      <PopupModal.Button buttonStyle="alt" buttonSize="sm" onClick={props.onClose}>
        No, take me back
      </PopupModal.Button>
    </PopupModal>
  )
}

export const DeleteAccount = ({ returnBack, setFieldToShow, subscriptions }: DeleteAccountProps) => {
  const analyticService = useContext(AnalyticServiceContext)
  const [showModal, setShowModal] = useState(false)
  const router = useRouter()
  const userService = useContext(UserServiceContext)

  const toasts = useContext(ToastContext)

  const isActiveOrPaused = subscriptions
    .map((subscription) => ['active', 'paused'].includes(subscription.subscription_status))
    .some((isActive) => isActive)

  const handleContinueToDelete = () => {
    if (isActiveOrPaused) {
      router.push('/deleteSubscription')
      returnBack()
    } else {
      setShowModal(true)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleConfirmDelete = () => {
    userService.startDeleteAccount().then((success) => {
      if (success) {
        analyticService.deleteAccount()
        setFieldToShow('delete-initiated')

        toasts.showToast({
          state: 'success',
          children: (
            <>{`The deletion process of your account has now been initiated. Please
refer to the email sent for further details.`}</>
          ),
          iconLeft: true,
        })
      }
      setShowModal(false)
    })
  }

  return (
    <MainContent>
      {showModal && (
        <DeleteConfirmModal
          onConfirm={handleConfirmDelete}
          onClose={handleCloseModal}
          showCloseButton={true}
          show={showModal}
        />
      )}
      <DeleteAccountPartWrapper>
        <BackIcon onClick={returnBack}>
          <GrandstandIcon icon="arrow_left" width="24px" />
        </BackIcon>
        <DeleteAccountHeading fontSize="4" mobileFontSize="3">
          Delete Account
        </DeleteAccountHeading>
      </DeleteAccountPartWrapper>
      <DeleteAccountDivider />
      <DeleteAccountContentWrapper>
        <DeleteAccountInfoWrapper>
          <DeleteAccountInfoContent fontSize="0">
            Once you delete your account, any remaining time on your subscription will be forfeited and you will not
            receive a refund. You will be unable to log in to FanDuel Sports Network or access any of your account
            details or settings.
          </DeleteAccountInfoContent>
        </DeleteAccountInfoWrapper>
        <DeleteAccountInfoWrapper>
          <DeleteButton buttonStyle="primary" buttonSize="sm" onClick={handleContinueToDelete}>
            <ButtonText fontSize="sm">continue to delete account</ButtonText>
          </DeleteButton>
        </DeleteAccountInfoWrapper>
      </DeleteAccountContentWrapper>
    </MainContent>
  )
}

export const AccountDeletionInitiated = ({ returnBack }: DeleteAccountProps) => {
  const userService = useContext(UserServiceContext)
  const router = useRouter()
  const toasts = useContext(ToastContext)
  const handleResendEmail = () => {
    userService.startDeleteAccount().then((success) => {
      if (success) {
        toasts.showToast({
          state: 'success',
          children: <>{'Email has been sent!'}</>,
          iconLeft: true,
        })
      }
    })
  }

  return (
    <MainContent>
      <DeleteAccountPartWrapper>
        <BackIcon onClick={returnBack}>
          <GrandstandIcon icon="arrow_left" width="24px" />
        </BackIcon>
        <DeleteAccountHeading fontSize="4" mobileFontSize="3">
          Delete Account
        </DeleteAccountHeading>
      </DeleteAccountPartWrapper>
      <DeleteAccountDivider />
      <DeleteAccountContentWrapper>
        <DeleteAccountInfoWrapper>
          <DeleteAccountInfoHeading fontSize="md">delete account initiated</DeleteAccountInfoHeading>
          <DeleteAccountInfoContent fontSize="0">
            The deletion process of your account has now been initiated. Please refer to the email sent for further
            details.
          </DeleteAccountInfoContent>
          <DeleteAccountInfoContent fontSize="0">Didn&apos;t receive an email?</DeleteAccountInfoContent>
        </DeleteAccountInfoWrapper>
        <DeleteAccountInfoWrapper>
          <DeleteButton buttonStyle="primary" buttonSize="sm" onClick={handleResendEmail}>
            <ButtonText fontSize="sm">resend email</ButtonText>
          </DeleteButton>
        </DeleteAccountInfoWrapper>
      </DeleteAccountContentWrapper>
    </MainContent>
  )
}
